// Curated classic albums fetched directly from Spotify.
// Used by the public landing + auth pages so covers render fast without
// hitting Discogs or the featured cache table.

import { createServerFn } from "@tanstack/react-start";

type ClassicAlbum = {
  id: string;
  discogsId: string;
  title: string;
  artist: string;
  year: number;
  genre: string;
  country: string;
  label: string;
  catalog: string;
  format: string;
  cover: string;
  tracklist: [];
  genres: string[];
};

// Hand-picked Spotify album IDs — global + brazilian classics.
const CLASSIC_IDS = [
  "4LH4d3cOWNNsVw41Gqt2kv", // Pink Floyd — The Dark Side of the Moon
  "0ETFjACtuP2ADo6LFhL6HN", // The Beatles — Abbey Road
  "2ANVost0y2y52ema1E9xAZ", // Michael Jackson — Thriller
  "2UJcKiJxNryhL050F5Z1Fk", // Nirvana — Nevermind
  "1bt6q2SruMsBtcerNVtpZB", // Fleetwood Mac — Rumours
  "0FZK97MXMm5mUQ8mtudjuK", // Stevie Wonder — Songs in the Key of Life
  "4m2880jivSbbyEGAKfITCa", // Daft Punk — Random Access Memories
  "6dVIqQ8qmQ5GBnJ9shOYGE", // Radiohead — OK Computer
  "748dZDqSZy6aPXKcI9H80u", // Kendrick Lamar — good kid, m.A.A.d city
  "097eYvf9NKjFnv4xA9s2oV", // Amy Winehouse — Back to Black
  "79dL7FLiJFOO0EoehUHQBv", // Tame Impala — Currents
  "1BZoqf8Zje5nGdwZhOjAtD", // Lauryn Hill — The Miseducation
  "5Dbax7G8SWrP9xyzkOvy2F", // Queen — A Night at the Opera
  "5tFRohaO5yEsuJZmiVdrz7", // Marvin Gaye — What's Going On
  "7DwBhrvQS7fyKw1HEW2cit", // Bob Marley & The Wailers — Legend
  "6X7B8GTlPbHaCq3Pf72FCO", // Tribalistas — Tribalistas
  "3DrgM5X3yX1JP1liNLAOHI", // The Beatles — Sgt. Pepper's
  "1To7kv722A8SpZF789MZy7", // U2 — The Joshua Tree
  "6mUdeDZCsExyJLMdAfDuwh", // Eagles — Hotel California
  "53NJVgDZliwAtIRoKupNYZ", // Caetano Veloso — Transa
  "2cWBwpqMsDJC1ZUwz813lo", // Tim Maia — Tim Maia
  "3T4tUhGYeRNVUGevb0wThu", // Ed Sheeran — Divide (modern classic)
  "1MKb4o4SeEvkAVTjeavoVF", // Chico Buarque — Construção
  "0M9ikq6BrqzJbi8DjyqyKw", // Os Mutantes — Os Mutantes
];

// Curated Brazilian classics — used by the hero for a local-vibe landing.
// 17 IDs so the hero (15 floating images) never repeats and has fallback coverage.
const BRAZILIAN_CLASSIC_IDS = [
  "6X7B8GTlPbHaCq3Pf72FCO", // Tribalistas — Tribalistas
  "53NJVgDZliwAtIRoKupNYZ", // Caetano Veloso — Transa
  "2cWBwpqMsDJC1ZUwz813lo", // Tim Maia — Tim Maia
  "1MKb4o4SeEvkAVTjeavoVF", // Chico Buarque — Construção
  "0M9ikq6BrqzJbi8DjyqyKw", // Os Mutantes — Os Mutantes
  "7JqTno9nqdXv7Trk5XlqW1", // Gilberto Gil — Gilberto Gil (1968)
  "0esHQqYklDPje00NUNq6py", // Jorge Ben — África Brasil
  "3SE9n6EaVOJ81KA1KPLUWS", // Elis & Tom
  "5risYG7klZCSLMNxB9dZhf", // Milton Nascimento & Lô Borges — Clube da Esquina
  "5pIlMNPZh4D9iJSoCfMzGi", // Novos Baianos — Acabou Chorare
  "0dtfLd8TK0aJECxnXgTAZw", // João Gilberto — Chega de Saudade
  "1c1rLO3NkLcGGBQvsp5mzu", // Cartola — Cartola (1976)
  "6XFQLDoWyOvlfHjFGmI0ts", // Secos & Molhados — Secos & Molhados
  "0fXZAZ5XejnxhgRV38SH5I", // Lô Borges — Lô Borges (1972)
  "5ZVLLME05cmciSZz4IGcZu", // Gal Costa — Gal Costa (1969)
  "48SnM2iORxlYTyCefwJZvs", // Rita Lee — Fruto Proibido (1975)
  "4OCJTpOLqSgysGY4yBCGhn", // Legião Urbana — Dois (1986)
];

type SpotifyAlbum = {
  id: string;
  name: string;
  artists: { name: string }[];
  release_date: string;
  genres: string[];
  label: string;
  images: { url: string }[];
};

let TOKEN: { value: string; exp: number } | null = null;
async function getToken(): Promise<string | null> {
  const id = process.env.SPOTIFY_CLIENT_ID;
  const secret = process.env.SPOTIFY_CLIENT_SECRET;
  if (!id || !secret) return null;
  if (TOKEN && TOKEN.exp > Date.now()) return TOKEN.value;
  const r = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: "Basic " + btoa(`${id}:${secret}`),
    },
    body: "grant_type=client_credentials",
  });
  if (!r.ok) return null;
  const j = (await r.json()) as { access_token: string; expires_in: number };
  TOKEN = { value: j.access_token, exp: Date.now() + (j.expires_in - 60) * 1000 };
  return TOKEN.value;
}

let CACHE: { data: ClassicAlbum[]; exp: number } | null = null;
const TTL = 1000 * 60 * 60 * 24; // 24h

export const getClassicSpotifyAlbums = createServerFn({ method: "GET" }).handler(
  async (): Promise<ClassicAlbum[]> => {
    if (CACHE && CACHE.exp > Date.now()) return CACHE.data;
    const token = await getToken();
    if (!token) return CACHE?.data ?? [];

    try {
      // /v1/albums supports up to 20 IDs per call.
      const out: ClassicAlbum[] = [];
      for (let i = 0; i < CLASSIC_IDS.length; i += 20) {
        const chunk = CLASSIC_IDS.slice(i, i + 20);
        const r = await fetch(
          `https://api.spotify.com/v1/albums?ids=${chunk.join(",")}&market=BR`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) continue;
        const j = (await r.json()) as { albums: (SpotifyAlbum | null)[] };
        for (const a of j.albums ?? []) {
          if (!a) continue;
          const cover = a.images?.[0]?.url ?? a.images?.[1]?.url;
          if (!cover) continue;
          out.push({
            id: a.id,
            discogsId: a.id,
            title: a.name,
            artist: a.artists?.map((x) => x.name).join(", ") ?? "",
            year: Number((a.release_date ?? "").slice(0, 4)) || 0,
            genre: a.genres?.[0] ?? "",
            country: "",
            label: a.label ?? "",
            catalog: "",
            format: "Vinyl",
            cover,
            tracklist: [],
            genres: a.genres ?? [],
          });
        }
      }
      if (out.length) CACHE = { data: out, exp: Date.now() + TTL };
      return out;
    } catch (err) {
      console.error("getClassicSpotifyAlbums failed", err);
      return CACHE?.data ?? [];
    }
  },
);

let BRAZILIAN_CACHE: { data: ClassicAlbum[]; exp: number } | null = null;

export const getBrazilianSpotifyAlbums = createServerFn({ method: "GET" }).handler(
  async (): Promise<ClassicAlbum[]> => {
    if (BRAZILIAN_CACHE && BRAZILIAN_CACHE.exp > Date.now()) return BRAZILIAN_CACHE.data;
    const token = await getToken();
    if (!token) return BRAZILIAN_CACHE?.data ?? [];

    try {
      const out: ClassicAlbum[] = [];
      for (let i = 0; i < BRAZILIAN_CLASSIC_IDS.length; i += 20) {
        const chunk = BRAZILIAN_CLASSIC_IDS.slice(i, i + 20);
        const r = await fetch(
          `https://api.spotify.com/v1/albums?ids=${chunk.join(",")}&market=BR`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) continue;
        const j = (await r.json()) as { albums: (SpotifyAlbum | null)[] };
        for (const a of j.albums ?? []) {
          if (!a) continue;
          const cover = a.images?.[0]?.url ?? a.images?.[1]?.url;
          if (!cover) continue;
          out.push({
            id: a.id,
            discogsId: a.id,
            title: a.name,
            artist: a.artists?.map((x) => x.name).join(", ") ?? "",
            year: Number((a.release_date ?? "").slice(0, 4)) || 0,
            genre: a.genres?.[0] ?? "",
            country: "",
            label: a.label ?? "",
            catalog: "",
            format: "Vinyl",
            cover,
            tracklist: [],
            genres: a.genres ?? [],
          });
        }
      }
      if (out.length) BRAZILIAN_CACHE = { data: out, exp: Date.now() + TTL };
      return out;
    } catch (err) {
      console.error("getBrazilianSpotifyAlbums failed", err);
      return BRAZILIAN_CACHE?.data ?? [];
    }
  },
);

